import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';

import {
  TtqQuestionExplosionWorkbench,
  type TtqExplosionSelection,
} from '../../features/ttqib/TtqQuestionExplosionWorkbench';
import {
  TtqWorkbenchSourceParticleGroup,
  type TtqWorkbenchSourceParticleGroupItem,
} from '../../features/ttqib/TtqWorkbenchSourceParticle';
import { TtqWorkbenchDerivedList, TtqWorkbenchFocusLayout } from '../../features/ttqib/TtqWorkbenchSplitLayout';
import { TtqWorkbenchToolbar, TtqWorkbenchToolbarAction } from '../../features/ttqib/TtqWorkbenchToolbar';
import { useTtqWorkbenchFocusEmphasis } from '../../features/ttqib/useTtqWorkbenchFocusEmphasis';
import { TtqWorkbenchShell } from '../../features/ttqib/ttqWorkbenchShared';
import { TTQ_QUESTION_CHAIN_VARIANTS } from '../../features/ttqib/ttqBehaviorCatalog';
import { parseTtqWorkbenchParticles } from '../../features/ttqib/ttqWorkbenchRoutes';
import {
  ttqWorkbenchService,
  type TtqWorkbenchDerivedItem,
} from '../../features/ttqib/ttqWorkbenchService';
import { fetchWorkbenchParticle } from '../../features/ttqib/ttqWorkbenchParticleCache';
import { resolveTtqParticleTitle } from '../../utils/ttqDisplay';
import { ttqService, type TtqSessionParticleEntry } from '../../services/ttqService';
import type { WorkspaceDerivedItem } from '../../services/kpWorkspaceService';

function toWorkspaceDerivedItems(items: TtqWorkbenchDerivedItem[]): WorkspaceDerivedItem[] {
  return items.map((item) => ({
    id: item.id,
    title: item.title || '变式题',
    summary: '',
    createdAt: item.createdAt || '',
    source: 'session' as const,
    question: item.question,
    answer: item.answer,
    particleId: item.particleId,
  }));
}

export function TtqQuestionsChainWorkbenchPage() {
  const { ttqaId = '' } = useParams();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;
  const [searchParams] = useSearchParams();
  const particleIds = useMemo(() => parseTtqWorkbenchParticles(searchParams), [searchParams]);
  const [activeParticleId, setActiveParticleId] = useState(particleIds[0] || '');
  const [entries, setEntries] = useState<TtqSessionParticleEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(true);
  const [selection, setSelection] = useState<TtqExplosionSelection>({ segmentIds: [], variantId: null });
  const [generating, setGenerating] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [derived, setDerived] = useState<TtqWorkbenchDerivedItem[]>([]);
  const [usedVariantIds, setUsedVariantIds] = useState<Set<string>>(new Set());
  const split = derived.length > 0;
  const { emphasis, setEmphasis } = useTtqWorkbenchFocusEmphasis(split);

  useEffect(() => {
    if (particleIds[0] && !activeParticleId) setActiveParticleId(particleIds[0]);
  }, [activeParticleId, particleIds]);

  useEffect(() => {
    let active = true;
    setLoadingEntries(true);
    void ttqService.getSession(ttqaId)
      .then((session) => {
        if (!active) return;
        const picked = (session.particles ?? []).filter((e) => particleIds.includes(e.particleId));
        setEntries(picked);
        setActiveParticleId((current) => {
          if (picked.length === 0) return current;
          if (picked.some((e) => e.particleId === current)) return current;
          return picked[0].particleId;
        });
      })
      .catch((e) => active && setLoadError(e instanceof Error ? e.message : '加载失败'))
      .finally(() => active && setLoadingEntries(false));
    return () => { active = false; };
  }, [particleIds, ttqaId]);

  const loadDerived = useCallback(async () => {
    if (!activeParticleId) return;
    try {
      const context = await ttqWorkbenchService.getQuestionsChainContext(ttqaId, activeParticleId);
      const previews = ttqWorkbenchService.mapDerivedPreviews(context);
      const enriched = await Promise.all(
        previews.map(async (item) => {
          const particle = await fetchWorkbenchParticle(item.particleId).catch(() => null);
          return {
            ...item,
            title: item.title || particle?.title || '变式题',
          };
        }),
      );
      setDerived(enriched);
      setUsedVariantIds(new Set(
        (context.questionChainVariants || [])
          .filter((item) => item.alreadyUsed)
          .map((item) => item.id),
      ));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '加载变式列表失败');
    }
  }, [activeParticleId, ttqaId]);

  useEffect(() => { void loadDerived(); }, [loadDerived]);

  const variantLabel = selection.variantId
    ? TTQ_QUESTION_CHAIN_VARIANTS.find((v) => v.id === selection.variantId)?.label
    : null;

  const groupItems = useMemo((): TtqWorkbenchSourceParticleGroupItem[] => (
    entries.map((entry, index) => ({
      particleId: entry.particleId,
      title: resolveTtqParticleTitle(entry, index),
    }))
  ), [entries]);

  const renderExplosion = useCallback(({ questionText }: { particleId: string; questionText: string }) => (
    <TtqQuestionExplosionWorkbench
      questionText={questionText}
      selection={selection}
      onSelectionChange={setSelection}
      disabledVariantIds={[...usedVariantIds]}
    />
  ), [selection, usedVariantIds]);

  const handleGenerate = async () => {
    if (!selection.variantId || !activeParticleId) return;
    if (usedVariantIds.has(selection.variantId)) {
      setActionError('该 L2 变式已在工作台生成过');
      return;
    }
    setGenerating(true);
    setActionError(null);
    try {
      await ttqWorkbenchService.runQuestionsChain(ttqaId, {
        sourceParticleIds: [activeParticleId],
        questionChainVariantId: selection.variantId,
        batchSize: 1,
        anchorText: selection.anchorText || undefined,
      });
      await loadDerived();
      setSelection({ segmentIds: [], variantId: null });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '生成失败');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <TtqWorkbenchShell
      ttqaId={ttqaId}
      title="多题多题工作台"
      angleId="questions_chain"
      loading={loadingEntries}
      error={loadError}
      restoreParticleIds={particleIds}
      toolbar={(
        <TtqWorkbenchToolbar
          showFocusToggle={split}
          leftLabel="源题"
          rightLabel="变式"
          emphasis={emphasis}
          onEmphasisChange={setEmphasis}
        >
          <TtqWorkbenchToolbarAction
            disabled={!selection.variantId || generating || !activeParticleId}
            loading={generating}
            onClick={() => { void handleGenerate(); }}
          >
            正式发起变式
          </TtqWorkbenchToolbarAction>
        </TtqWorkbenchToolbar>
      )}
    >
      {actionError ? <p className="text-sm text-danger">{actionError}</p> : null}
      <p className="text-xs text-muted">已选 {particleIds.length} 颗源题，逐题进行 GO-ON 变式衍生</p>
      {variantLabel ? (
        <p className="text-xs text-muted">当前 L2：{variantLabel}</p>
      ) : null}

      {entries.length > 0 ? (
        <TtqWorkbenchFocusLayout
          split={split}
          emphasis={emphasis}
          source={(
            <TtqWorkbenchSourceParticleGroup
              items={groupItems}
              activeParticleId={activeParticleId}
              onActiveParticleChange={(id) => {
                if (id === activeParticleId) return;
                setActiveParticleId(id);
                setSelection({ segmentIds: [], variantId: null });
              }}
              renderExplosion={renderExplosion}
            />
          )}
          derived={<TtqWorkbenchDerivedList title="变式颗粒" items={toWorkspaceDerivedItems(derived)} emptyText="暂无变式题" returnTo={returnTo} />}
        />
      ) : null}
    </TtqWorkbenchShell>
  );
}
